<template>
  <div class="customer-info-step wizard-step">
    <div class="wizard-step-body">
      <!-- 顧客紐付けセクション -->
      <section class="wizard-section-card">
        <div class="wizard-section-body">
          <div
            v-if="selectedCustomerIds.length === 0"
            class="text--secondary text-caption mb-2"
          >
            顧客の紐付けは任意です
          </div>

          <div
            v-for="id in selectedCustomerIds"
            :key="`customer-card-${id}`"
            class="selected-customer-card mb-2"
          >
            <div class="d-flex align-center">
              <v-icon class="mr-2">
                {{ icons.mdiAccountCircle }}
              </v-icon>
              <span class="font-weight-medium">{{ getCustomerName(id) }}</span>
              <v-spacer />
              <v-btn
                icon
                small
                @click="removeCustomerId(id)"
              >
                <v-icon small>
                  {{ icons.mdiClose }}
                </v-icon>
              </v-btn>
            </div>
            <customer-custom-field-list
              :customer="getCustomer(id)"
              :custom-fields="customFields"
            />
          </div>

          <v-btn
            outlined
            large
            color="primary"
            @click="customerDialogVisible = true"
          >
            <v-icon left>
              {{ icons.mdiPlus }}
            </v-icon>
            顧客を選択 / 新規登録
          </v-btn>
        </div>
      </section>

      <!-- 顧客タグセクション (卓単位のタグ付け・タグ未登録の店舗では非表示) -->
      <customer-tag-chips
        :value="customerTableTaggings"
        @input="onTaggingsUpdated"
      />

      <!-- 指名 / リファラル サブタブ（カード内スクロール） -->
      <section
        v-if="showNominationTab || showReferralTab"
        class="wizard-section-card wizard-section-card--fill"
      >
        <div class="wizard-section-header pa-0 nomination-tabs-header">
          <v-tabs
            v-model="activeTab"
            height="44"
            background-color="transparent"
            class="nomination-tabs"
            :slider-size="0"
          >
            <v-tab
              v-if="showNominationTab"
              class="nomination-tab"
            >
              指名
            </v-tab>
            <v-tab
              v-if="showReferralTab"
              class="nomination-tab"
            >
              リファラル
            </v-tab>
          </v-tabs>
        </div>

        <v-tabs-items
          :value="activeTab"
          class="wizard-section-scroll"
        >
          <!-- 指名 -->
          <v-tab-item v-if="showNominationTab">
            <div class="pa-3">
              <v-text-field
                v-model="castSearch"
                label="キャストの検索"
                outlined
                dense
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                class="mb-3"
              />
              <nomination-radio-row
                v-for="cast in filteredCasts"
                :key="`nrr-${cast.id}`"
                :cast="cast"
                :nomination-defs="nominationDefs"
                :selected-vendible-ids="getNominationVendibleIdsFor(cast.id)"
                :free-back="getFreeBackFor(cast.id)"
                @change="onNominationChange(cast, $event)"
                @free-back-change="onFreeBackChange(cast, $event)"
              />
              <div
                v-if="filteredCasts.length === 0"
                class="text-center text--secondary py-4"
              >
                該当するキャストが見つかりません
              </div>
            </div>
          </v-tab-item>

          <!-- リファラル -->
          <v-tab-item v-if="showReferralTab">
            <div class="pa-3">
              <v-text-field
                v-model="referralUserSearch"
                label="紹介者の検索"
                outlined
                dense
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                class="mb-3"
              />
              <referral-user-row
                v-for="u in filteredReferralUsers"
                :key="`rur-${u.id}`"
                :user="u"
                :referral-options="referralOptions"
                :selected-referral-id="getReferralIdForUser(u.id)"
                @change="onReferralForUserChange(u, $event)"
              />
              <div
                v-if="filteredReferralUsers.length === 0"
                class="text-center text--secondary py-4"
              >
                該当する紹介者が見つかりません
              </div>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </section>

      <section
        v-else
        class="wizard-section-card"
      >
        <div class="wizard-section-body text-center text--secondary">
          指名・リファラル設定は不要です（キャストまたはマスタが未登録）
        </div>
      </section>
    </div>

    <!-- 顧客選択ダイアログ -->
    <v-dialog
      v-model="customerDialogVisible"
      max-width="600"
    >
      <v-card>
        <v-card-title>顧客を検索 / 新規登録</v-card-title>
        <!-- 検索バー + 新規登録ボタンを上部に sticky 固定 -->
        <div class="customer-dialog-header pa-4 pt-2">
          <div
            class="d-flex align-center"
            style="gap: 8px;"
          >
            <v-text-field
              v-model="customerSearch"
              label="名前で検索"
              outlined
              dense
              hide-details
              autofocus
              class="flex-grow-1"
            />
            <v-btn
              color="primary"
              :disabled="!customerSearch.trim() || isCreating"
              :loading="isCreating"
              @click="handleCreateNewCustomer"
            >
              <v-icon left>
                {{ icons.mdiPlus }}
              </v-icon>
              <template v-if="customerSearch.trim()">
                「{{ customerSearch.trim() }}」で新規登録
              </template>
              <template v-else>
                新規登録
              </template>
            </v-btn>
          </div>
        </div>
        <v-card-text class="pt-0">
          <v-list
            two-line
            class="customer-result-list"
          >
            <v-list-item
              v-for="c in filteredCustomers"
              :key="c.id"
              @click="addCustomerId(c.id)"
            >
              <v-list-item-content>
                <v-list-item-title
                  class="d-flex align-center"
                  style="gap: 8px;"
                >
                  <span class="font-weight-medium">{{ c.name }}</span>
                  <v-chip
                    v-if="c.memberNumber"
                    x-small
                    outlined
                    color="primary"
                  >
                    No.{{ c.memberNumber }}
                  </v-chip>
                  <v-chip
                    v-for="tag in (c.customerTags || []).slice(0, 2)"
                    :key="`tag-${tag.id || tag.name}`"
                    x-small
                    label
                    color="grey lighten-3"
                  >
                    {{ tag.name || tag }}
                  </v-chip>
                </v-list-item-title>
                <v-list-item-subtitle
                  class="d-flex align-center text-caption"
                  style="gap: 12px;"
                >
                  <span v-if="c.visitCount != null">来店 {{ c.visitCount }}回</span>
                  <span v-if="c.lastVisitedAt">前回 {{ formatDate(c.lastVisitedAt) }}</span>
                  <span v-if="c.birthday">誕生日 {{ formatBirthday(c.birthday) }}</span>
                </v-list-item-subtitle>
                <customer-custom-field-list
                  inline
                  :customer="c.raw || c"
                  :custom-fields="customFields"
                />
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="filteredCustomers.length === 0 && !isSearching"
              disabled
            >
              <v-list-item-content class="text--secondary">
                {{ customerSearch ? '該当する顧客が見つかりません' : '名前を入力してください' }}
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="isSearching"
              disabled
            >
              <v-list-item-content class="text--secondary">
                検索中…
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="hasMoreCustomers"
              disabled
            >
              <v-list-item-content class="text--secondary text-caption">
                他にも候補があります。名前で絞り込んでください
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="customerDialogVisible = false"
          >
            閉じる
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  ref, computed, watch, inject, getCurrentInstance, onMounted,
} from '@vue/composition-api'
import { mdiPlus, mdiClose, mdiAccountCircle } from '@mdi/js'
import CustomerApi from '@/api/v2/admin/customer-management/Customer'
import useCustomerCustomFields from '@/views/v2-temp/common/customer-management/composables/useCustomerCustomFields'
import CustomerTagChips from '../parts/CustomerTagChips.vue'
import NominationRadioRow from '../parts/NominationRadioRow.vue'
import ReferralUserRow from '../parts/ReferralUserRow.vue'
import CustomerCustomFieldList from '../parts/CustomerCustomFieldList.vue'

// data:[{id, attributes:{name,...}}] と {id, name,...} の両方を受け取れるように
const flatten = item => {
  if (!item) return null
  if (item.attributes) return { id: item.id, ...item.attributes, raw: item }

  return { ...item, raw: item }
}

export default {
  name: 'CustomerInfoStep',
  components: {
    CustomerTagChips,
    NominationRadioRow,
    ReferralUserRow,
    CustomerCustomFieldList,
  },
  props: {
    nominations: { type: Array, default: () => [] },
    users: { type: Array, default: () => [] },
    referrals: { type: Array, default: () => [] },
    selectedCustomerIds: { type: Array, default: () => [] },
    castNominations: { type: Array, default: () => [] }, // [{ castId, vendibleId, sellingPrice }]
    referralRowsValue: { type: Array, default: () => [] }, // [{ referralId, userId }]
    customerTableTaggings: { type: Array, default: () => [] }, // [{ id, customerTagId, tableId, _destroy, attributes }]
  },
  emits: [
    'update:selectedCustomerIds',
    'update:castNominations',
    'update:referralRowsValue',
    'update:customerTableTaggings',
  ],
  setup(props, { emit }) {
    const vm = getCurrentInstance()?.proxy
    const activeTab = ref(0)
    const customerDialogVisible = ref(false)
    const customerSearch = ref('')
    const isCreating = ref(false)
    const INITIAL_DISPLAY_LIMIT = 20
    const SEARCH_DISPLAY_LIMIT = 50
    const castSearch = ref('')
    const referralUserSearch = ref('')

    // カスタムフィールド定義の取得
    const { customFields, getCustomFields } = useCustomerCustomFields(vm || {})
    onMounted(() => {
      try { getCustomFields() } catch (_) { /* noop in tests */ }
    })

    // 既存 OrderCard.vendibleRecipientUsers と同じロジック
    const usersWithTimecard = inject('usersWithTimecard', ref([]))

    const normalizedAllCasts = computed(() => props.users
      .map(flatten)
      .filter(u => u && u.role === 'cast'))

    const normalizedWorkingCasts = computed(() => (usersWithTimecard.value || [])
      .map(flatten)
      .filter(u => u && u.role === 'cast'))

    const normalizedUsers = computed(() => (
      normalizedWorkingCasts.value.length > 0
        ? normalizedWorkingCasts.value
        : normalizedAllCasts.value
    ))

    const filteredCasts = computed(() => {
      const q = (castSearch.value || '').trim().toLowerCase()
      if (!q) return normalizedUsers.value

      return normalizedUsers.value.filter(c => (c.name || '').toLowerCase().includes(q))
    })

    const nominationDefs = computed(() => props.nominations
      .map(flatten)
      .filter(Boolean)
      .map(n => ({
        id: n.id,
        name: n.name || '指名',
        sellingPrice: n.sellingPrice || 0,
      })))

    const referralOptions = computed(() => props.referrals
      .map(flatten)
      .filter(Boolean)
      .map(r => ({ id: r.id, name: r.name || `referral#${r.id}` })))

    // リファラルの紹介者候補は alliance / cast / waiter の3ロール全て
    // ロールごとに「出勤者あり→出勤者、なし→そのロール全員」のフォールバック (旧 ReferralCard と同等)
    const REFERRAL_ROLES = ['alliance', 'cast', 'waiter']
    const referralUsers = computed(() => {
      const allByRole = REFERRAL_ROLES.reduce((acc, role) => {
        acc[role] = props.users.map(flatten).filter(u => u && u.role === role)

        return acc
      }, {})
      const workingByRole = REFERRAL_ROLES.reduce((acc, role) => {
        acc[role] = (usersWithTimecard.value || [])
          .map(flatten).filter(u => u && u.role === role)

        return acc
      }, {})

      return REFERRAL_ROLES.flatMap(role => (
        workingByRole[role].length > 0 ? workingByRole[role] : allByRole[role]
      ))
    })

    const filteredReferralUsers = computed(() => {
      const q = (referralUserSearch.value || '').trim().toLowerCase()
      if (!q) return referralUsers.value

      return referralUsers.value.filter(u => (u.name || '').toLowerCase().includes(q))
    })

    const showNominationTab = computed(() => (
      nominationDefs.value.length > 0 && normalizedUsers.value.length > 0
    ))
    const showReferralTab = computed(() => (
      referralOptions.value.length > 0 && referralUsers.value.length > 0
    ))

    // 検索結果と「選択済み顧客の名前キャッシュ」を統合管理
    const searchResults = ref([])
    const knownCustomers = ref({}) // { [id]: { id, name, raw } }
    const isSearching = ref(false)

    const fetchCustomers = async query => {
      isSearching.value = true
      try {
        const res = await CustomerApi.getCustomers({ q: (query || '').trim() })
        const list = (res?.data?.data || []).map(flatten).filter(Boolean)
        searchResults.value = list
        list.forEach(c => {
          knownCustomers.value = { ...knownCustomers.value, [c.id]: c }
        })
      } catch (_) {
        searchResults.value = []
      } finally {
        isSearching.value = false
      }
    }

    watch(customerDialogVisible, v => {
      if (v) fetchCustomers(customerSearch.value)
    })
    watch(customerSearch, q => {
      if (customerDialogVisible.value) fetchCustomers(q)
    })

    const displayLimit = computed(() => (
      customerSearch.value.trim() ? SEARCH_DISPLAY_LIMIT : INITIAL_DISPLAY_LIMIT
    ))
    const filteredCustomers = computed(() => searchResults.value.slice(0, displayLimit.value))
    const hasMoreCustomers = computed(() => searchResults.value.length > displayLimit.value)

    const getCustomer = id => knownCustomers.value[id] || null
    const getCustomerName = id => {
      const c = knownCustomers.value[id]

      return c?.name || `顧客#${id}`
    }

    const addCustomerId = id => {
      if (props.selectedCustomerIds.includes(id)) return
      emit('update:selectedCustomerIds', [...props.selectedCustomerIds, id])
      customerDialogVisible.value = false
      customerSearch.value = ''
    }
    const removeCustomerId = id => {
      emit('update:selectedCustomerIds', props.selectedCustomerIds.filter(x => x !== id))
    }

    const newCustomerName = ref('')

    const handleCreateNewCustomer = async () => {
      const name = (newCustomerName.value || customerSearch.value || '').trim()
      if (!name) return
      isCreating.value = true
      try {
        const res = await CustomerApi.createCustomer({ name })
        const created = res?.data?.data
        const flat = flatten(created)
        if (flat) {
          knownCustomers.value = { ...knownCustomers.value, [flat.id]: flat }
          emit('update:selectedCustomerIds', [...props.selectedCustomerIds, flat.id])
          if (vm?.$toast?.success) vm.$toast.success(`顧客「${name}」を作成しました`)
          customerSearch.value = ''
          customerDialogVisible.value = false
        }
      } catch (_) {
        if (vm?.$toast?.error) vm.$toast.error('顧客の作成に失敗しました')
      } finally {
        isCreating.value = false
      }
    }

    const onTaggingsUpdated = taggings => {
      emit('update:customerTableTaggings', taggings)
    }

    // 指名: 同一キャストで複数選択可。castNominations は (castId, vendibleId) のペアで管理
    const getNominationVendibleIdsFor = castId => (
      (props.castNominations || [])
        .filter(n => n.castId === castId)
        .map(n => n.vendibleId)
    )
    // フリーバック(フリー客→指名移行)はキャスト行の FB ボタンで指定する。
    // バックエンドは販売履歴単位のフラグだが、件数集計を1キャスト1件に保つため
    // そのキャストの選択中指名のうち指名マスタ順で先頭の1件だけに付与する
    const getFreeBackFor = castId => (props.castNominations || [])
      .some(n => n.castId === castId && n.freeBack)
    const applyFreeBack = (rows, castId, on) => {
      const order = nominationDefs.value.map(d => d.id)
      const first = rows
        .filter(n => n.castId === castId)
        .sort((a, b) => order.indexOf(a.vendibleId) - order.indexOf(b.vendibleId))[0]

      return rows.map(n => (
        n.castId === castId
          ? { ...n, freeBack: !!(on && first && n.vendibleId === first.vendibleId) }
          : n
      ))
    }
    const onFreeBackChange = (cast, freeBack) => {
      emit('update:castNominations', applyFreeBack(props.castNominations || [], cast.id, freeBack))
    }
    const onNominationChange = (cast, { vendibleId, def, selected }) => {
      const others = (props.castNominations || [])
        .filter(n => !(n.castId === cast.id && n.vendibleId === vendibleId))
      const next = selected
        ? [...others, {
          castId: cast.id, vendibleId, sellingPrice: def?.sellingPrice || 0, freeBack: false,
        }]
        : others
      // 指名の追加・解除後も FB の付与先を「先頭1件」に保つ(付与先が解除されたら残りの先頭へ移す)
      emit('update:castNominations', applyFreeBack(next, cast.id, getFreeBackFor(cast.id)))
    }

    // リファラル: ユーザー単位で referralId を1つ保持
    const referralRows = computed(() => props.referralRowsValue || [])
    const getReferralIdForUser = userId => {
      const found = referralRows.value.find(r => r.userId === userId)

      return found?.referralId || null
    }
    const onReferralForUserChange = (user, { referralId }) => {
      const others = referralRows.value.filter(r => r.userId !== user.id)
      const next = referralId
        ? [...others, { referralId, userId: user.id }]
        : others
      emit('update:referralRowsValue', next)
    }

    const formatDate = iso => {
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ''
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')

      return `${y}/${m}/${day}`
    }

    const formatBirthday = iso => {
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ''
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')

      return `${m}/${day}`
    }

    return {
      activeTab,
      customerDialogVisible,
      customerSearch,
      newCustomerName,
      isCreating,
      hasMoreCustomers,
      isSearching,
      castSearch,
      referralUserSearch,
      customFields,
      formatDate,
      formatBirthday,
      normalizedUsers,
      filteredCasts,
      nominationDefs,
      referralOptions,
      filteredReferralUsers,
      showNominationTab,
      showReferralTab,
      filteredCustomers,
      getCustomer,
      getCustomerName,
      addCustomerId,
      removeCustomerId,
      handleCreateNewCustomer,
      onTaggingsUpdated,
      getNominationVendibleIdsFor,
      onNominationChange,
      getFreeBackFor,
      onFreeBackChange,
      getReferralIdForUser,
      onReferralForUserChange,
      icons: { mdiPlus, mdiClose, mdiAccountCircle },
    }
  },
}
</script>

<style scoped>
.selected-customer-card {
  border: 1px solid rgba(127, 127, 127, 0.32);
  border-radius: 6px;
  padding: 8px 12px;
  background: transparent;
}
.customer-dialog-header {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--v-background-base);
  border-bottom: 1px solid rgba(127, 127, 127, 0.12);
}
/* 指名/リファラルカードのヘッダー: タブだけでヘッダー帯を構成 */
.nomination-tabs-header {
  padding: 6px 8px !important;
  border-bottom: 1px solid rgba(127, 127, 127, 0.24);
  background: transparent !important;
}
.nomination-tabs {
  flex: 0 0 auto;
}
/* タブ自体をピル型に */
.nomination-tabs :deep(.v-tabs-bar) {
  background: transparent !important;
}
/* タブ詳細スタイルは TableCreateWizard.vue のグローバル CSS で定義
   （scoped + :deep の制限を回避し、Vuetify の active color と確実に競合解決） */
.nomination-tabs :deep(.v-tabs-slider-wrapper) {
  display: none;
}
</style>
